import { z } from 'zod';

/**
 * The Nest contract (audit 2026-10-13): what the web apps and the backends must agree on, in one place, with no
 * React and no server code, so both import it — Core role → app permissions, staff roles, error codes and the
 * problem shape, and product codes. The backends enforce permissions from the same map the shell uses to show apps
 * and menus (CONVENTIONS: "the backend enforces the same").
 */

/* ---- permissions: namespaced per app (A-7) ------------------------------------------------------------------- */

export const PERMISSIONS = {
  payments: [
    'payments.read', 'payments.refund', 'payments.capture', 'payments.approve', 'payments.disputes.respond',
    'payments.payouts.retry', 'payments.recon.resolve', 'payments.reports.run', 'payments.settings.manage', 'payments.audit.read',
    // Pay links: every customer role reads them; admins and users create, extend and cancel them (A-40).
    'payments.paylinks.read', 'payments.paylinks.manage',
    // The organization's pay-link brand and defaults: admins only; users manage links, not their set-up (2026-11-02).
    'payments.paylinks.configure',
    // Card terminals: organization and business-unit admins manage their own unit's readers, as in Retail (owner,
    // 2026-10-22, A-37); Nest staff keep first-time set-up and troubleshooting (payments.setup.manage).
    'payments.terminals.manage',
    // Payment methods per channel (A-38): the organization default and locks by organization admins, a unit's override
    // by organization admins or that unit's admins; Core payer-config.
    'payments.methods.manage',
  ],
  /**
   * Nest staff in Payments. Never granted to customer roles. Staff read and troubleshoot; they never charge, refund,
   * send to customers or change transactions (owner, 2026-10-14): those are the tenant's own people's (payments.*).
   */
  // payments.recon.run: start reconciliation runs and resolve their exceptions (super and platform admins; support
  // admins only read, with payments.ledger.read, as Core's reconciliation:read).
  paymentsStaff: ['payments.platform.read', 'payments.ledger.read', 'payments.setup.manage', 'payments.recon.run'],
  /**
   * Nest staff only, in Organization (A-29): the tenant record and set-up, creating and closing business units, changing
   * an organization's or unit's status, and deleting a person outright. Never granted to customer roles.
   */
  // org.platform.manage: change a tenant's people and business units from Organization's staff view (owner, 2026-10-13).
  orgStaff: ['org.setup.manage', 'org.bu.create', 'org.bu.close', 'org.status.manage', 'org.users.delete', 'org.platform.manage'],
  /** Customers in Organization, one permission per action (A-29). Scope (organization or own unit) is checked as well. */
  org: [
    'org.read', 'org.profile.update', 'org.bu.update',
    'org.users.read', 'org.users.invite', 'org.users.update', 'org.users.suspend', 'org.users.roles.assign',
    'org.roles.read', 'org.security.manage', 'org.audit.read',
    // The organization's own email and SMS templates (owner, 2026-11-01: org admins set them up, not staff).
    'org.templates.read', 'org.templates.manage',
  ],
  // Reads (staff troubleshoot): tenants, onboarding runs, the platform catalogue (providers, roles and permissions).
  // Changes: run onboarding, manage providers, configure the platform (roles' permissions).
  ops: [
    'ops.tenants.read', 'ops.onboarding.read', 'ops.platform.read', 'ops.onboarding.run', 'ops.view_as', 'ops.providers.manage',
    'ops.connectors.manage', 'ops.platform.config', 'ops.monitoring.read', 'ops.services.control', 'ops.audit.read',
  ],
  /**
   * SmartBill (product app, owner 2026-10-19): bills, collecting a bill, payers, its own settings. Money itself lives in
   * Payments. Customer roles only: Nest staff read SmartBill in the staff view and never act (A-34). Until Core has
   * product roles (SmartBill clerk, viewer…), these ride on today's roles: admins act and set up, users operate (A-40),
   * viewers read.
   */
  smartbill: ['smartbill.read', 'smartbill.bills.manage', 'smartbill.collect', 'smartbill.settings.manage', 'smartbill.ingest'],
  /** Super admins only: deleting a tenant (owner, 2026-10-13: organization and unit deletion is super-admin owned). */
  opsSuper: ['ops.tenants.offboard'],
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS][number];

/** Permissions only Nest staff roles hold. */
const STAFF_PERMISSIONS = new Set<string>([...PERMISSIONS.ops, ...PERMISSIONS.opsSuper, ...PERMISSIONS.paymentsStaff, ...PERMISSIONS.orgStaff]);
export const isStaffPermission = (perm: Permission) => STAFF_PERMISSIONS.has(perm);

export type Audience = 'customer' | 'staff';

const P = PERMISSIONS;

/** What every staff role may read: tenants and their payments, onboarding runs, the platform catalogue, health, audit. */
const STAFF_READS = ['ops.tenants.read', 'ops.onboarding.read', 'ops.platform.read', 'ops.monitoring.read', 'ops.audit.read', 'payments.platform.read'] as const satisfies readonly Permission[];

/** What org and unit users do (owner, 2026-11-02): the day-to-day operations, never set-up. */
const USER_OPERATIONS = ['payments.capture', 'payments.refund', 'payments.paylinks.read', 'payments.paylinks.manage', 'smartbill.bills.manage', 'smartbill.collect'] as const satisfies readonly Permission[];
/** What an organization or business-unit admin does with their payments, in their own scope. */
const PAYMENT_ACTIONS = ['payments.capture', 'payments.refund', 'payments.paylinks.read', 'payments.paylinks.manage', 'payments.terminals.manage', 'payments.methods.manage', 'payments.paylinks.configure'] as const satisfies readonly Permission[];

/**
 * Core's roles (in the person's access token) and what each grants in Nest's apps. Core is the authority for who
 * holds which role; this map decides what each role may see and do in Nest, for the shell and the backends alike.
 * Roles not listed grant nothing. `org_*` roles cover every business unit in the organization, `bu_*` roles one unit,
 * staff roles the platform. Admins act and set up; users do day-to-day operations (A-40); viewers read.
 */
export const CORE_ROLES: Readonly<Record<string, { name: string; audience: Audience; permissions: readonly Permission[] }>> = {
  // Admins act on their payments (capture, refund, cancel...) and pay links (owner, 2026-10-14; Core grants the same).
  org_admin: { name: 'Organization admin', audience: 'customer', permissions: ['payments.read', 'payments.audit.read', ...PAYMENT_ACTIONS, ...P.org, ...P.smartbill] },
  // Users operate day to day (owner, 2026-11-02): bills, collecting, payments, refunds and sends, in their org or unit.
  // Admin-only stays with admins: people, templates, organization and unit settings, payment rules and methods.
  org_user: { name: 'Organization user', audience: 'customer', permissions: ['payments.read', ...USER_OPERATIONS, 'org.read', 'smartbill.read'] },
  org_viewer: { name: 'Organization viewer', audience: 'customer', permissions: ['payments.read', 'payments.paylinks.read', 'org.read', 'smartbill.read'] },
  // Manages their own unit: its details and people (owner, 2026-10-13); never organization-wide settings.
  bu_admin: {
    name: 'Business unit admin', audience: 'customer',
    permissions: [
      'payments.read', ...PAYMENT_ACTIONS, 'org.read', 'org.bu.update', 'org.users.read', 'org.users.invite', 'org.users.update',
      'org.users.suspend', 'org.users.roles.assign', ...P.smartbill,
    ],
  },
  bu_user: { name: 'Business unit user', audience: 'customer', permissions: ['payments.read', ...USER_OPERATIONS, 'org.read', 'smartbill.read'] },
  bu_viewer: { name: 'Business unit viewer', audience: 'customer', permissions: ['payments.read', 'payments.paylinks.read', 'org.read', 'smartbill.read'] },
  super_admin: { name: 'Super admin', audience: 'staff', permissions: [...P.ops, ...P.opsSuper, ...P.paymentsStaff, ...P.orgStaff] },
  platform_admin: { name: 'Platform admin', audience: 'staff', permissions: [...P.ops, ...P.paymentsStaff, ...P.orgStaff] },
  // Troubleshoots only (owner, 2026-10-14): reads tenants, their payments, people and settings, onboarding runs and the
  // platform catalogue; changes nothing. Core matches (V43; onboarding read only, #1130).
  support_admin: {
    name: 'Support admin', audience: 'staff',
    permissions: [...STAFF_READS, 'ops.view_as', 'payments.ledger.read'],
  },
  platform_user: { name: 'Platform user', audience: 'staff', permissions: [...STAFF_READS] },
};

/** Core's staff roles: the only roles that make a platform session (A-26). Derived from the map, never listed twice. */
export const STAFF_ROLES: ReadonlySet<string> = new Set(Object.entries(CORE_ROLES).filter(([, r]) => r.audience === 'staff').map(([id]) => id));

/** What a set of Core roles may do in Nest. */
export function permissionsOfRoles(roles: readonly string[]): Set<Permission> {
  const out = new Set<Permission>();
  for (const role of roles) CORE_ROLES[role.toLowerCase()]?.permissions.forEach((p) => out.add(p));
  return out;
}

export function audienceOfRoles(roles: readonly string[]): Audience {
  return roles.some((r) => STAFF_ROLES.has(r.toLowerCase())) ? 'staff' : 'customer';
}

/** A Core role's display name; unknown roles are shown readably, never guessed. */
export function roleName(role: string): string {
  const r = CORE_ROLES[role.toLowerCase()];
  if (r) return r.name;
  const s = role.replace(/[_-]+/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/* ---- who may manage whom (A-29) ------------------------------------------------------------------------------- */

const ORG_ROLE = /^org_/i;
const BU_ROLE = /^bu_/i;
const CUSTOMER_ROLES = Object.entries(CORE_ROLES).filter(([, r]) => r.audience === 'customer').map(([id]) => id);

/** The person acting, as their session knows them. */
export interface Actor {
  level: 'org' | 'bu' | 'platform';
  orgId: string | null;
  /** Their own unit (business-unit people); null for organization people and staff. */
  buId: string | null;
  roles: readonly string[];
}

/** The person acted on, as Core returns them. */
export interface Person {
  orgId: string | null;
  buId: string | null;
  roles: readonly string[];
}

/**
 * The roles an actor may give someone (no one grants more than they hold): staff and organization admins any
 * customer role; business-unit admins business-unit roles only. Everyone else none.
 */
export function assignableRoles(actor: Actor): Set<string> {
  const roles = actor.roles.map((r) => r.toLowerCase());
  if (actor.level === 'platform' && audienceOfRoles(roles) === 'staff') return new Set(CUSTOMER_ROLES);
  if (actor.level === 'org' && roles.includes('org_admin')) return new Set(CUSTOMER_ROLES);
  if (actor.level === 'bu' && roles.includes('bu_admin')) return new Set(CUSTOMER_ROLES.filter((r) => BU_ROLE.test(r)));
  return new Set();
}

/**
 * Whether an actor may manage a person at all (edit, suspend, change roles, resend an invitation), before the action's
 * own permission is checked. Organization admins: anyone in their organization. Business-unit admins: people of their
 * own unit who hold only business-unit roles (never an organization-wide person). The reach comes from holding
 * org_admin or bu_admin itself. Staff: customers of any
 * organization (Ops). No one manages staff here. Acting on oneself is refused by the write routes (A-29).
 */
export function mayManagePerson(actor: Actor, person: Person): boolean {
  if (audienceOfRoles(person.roles) === 'staff') return false;
  if (actor.level === 'platform') return audienceOfRoles(actor.roles) === 'staff';
  if (!actor.orgId || person.orgId !== actor.orgId) return false;
  // The reach comes from the admin role itself, never from mixing roles: an organization-level person who is only a
  // business-unit admin (e.g. bu_admin + org_user) manages no one organization-wide.
  const roles = actor.roles.map((r) => r.toLowerCase());
  if (actor.level === 'org') return roles.includes('org_admin');
  return roles.includes('bu_admin') && actor.buId !== null && person.buId === actor.buId && person.roles.length > 0 && person.roles.every((r) => BU_ROLE.test(r) && !ORG_ROLE.test(r));
}

/* ---- products --------------------------------------------------------------------------------------------------- */

export type ProductName = 'SmartBill' | 'Retail' | 'Giving' | 'Stay';
const PRODUCTS: Readonly<Record<string, ProductName>> = { smartbill: 'SmartBill', retail: 'Retail', giving: 'Giving', stay: 'Stay', nest: 'Stay' };
/** The suite product of a Core product code (Core's hospitality code "nest" is the Stay app, A-24); null if none. */
export const productOf = (code: string | null | undefined): ProductName | null => (code ? (PRODUCTS[code.toLowerCase()] ?? null) : null);

/* ---- errors (RFC 9457 problem+json) ----------------------------------------------------------------------------- */

/**
 * Codes every Nest backend returns. NOT_AVAILABLE: the feature's Core API isn't shipped yet (an agreed contract Core
 * hasn't served); screens show it as "Coming soon", never as a failure.
 */
export const SERVER_ERROR_CODES = [
  'AUTH_REQUIRED', 'SIGN_IN_FAILED', 'NOT_ORGANIZATION_USER', 'CSRF_FAILED', 'SCOPE_INSUFFICIENT', 'PERMISSION_DENIED',
  'NOT_FOUND', 'CONFLICT', 'VALIDATION_FAILED', 'RATE_LIMITED', 'UPSTREAM_UNAVAILABLE', 'INTERNAL', 'SELF_ACTION', 'LAST_ADMIN',
  'NOT_AVAILABLE',
] as const;
export type ServerErrorCode = (typeof SERVER_ERROR_CODES)[number];
/** Plus BAD_RESPONSE: client side, a response failed its schema. */
export const ERROR_CODES = [...SERVER_ERROR_CODES, 'BAD_RESPONSE'] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

const isErrorCode = (c: string): c is ErrorCode => (ERROR_CODES as readonly string[]).includes(c);

/**
 * A problem as the web reads it. Tolerant: a code a newer backend adds reads as INTERNAL (with its title and status)
 * instead of failing the whole response, so web and backends can be released separately.
 */
export const Problem = z.object({
  title: z.string(),
  status: z.number().int(),
  code: z.string().transform((c): ErrorCode => (isErrorCode(c) ? c : 'INTERNAL')),
  requestId: z.string().optional(),
  fields: z.record(z.string(), z.string()).optional(),
});
export type Problem = z.infer<typeof Problem>;
